import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LuCheck } from "react-icons/lu";
import { Badge, Button, cn } from "../../../components/ui";
import type { BadgeTone } from "../../../components/ui";
import { planDetails } from "../../../constants/freePlan";
import { planoChaveLabel, assinaturaStatusLabel, faturaStatusLabel } from "../../../constants/billing";
import { useSession } from "../../../context/SessionContext";
import { useAccess } from "../../../hooks/useAccess";
import { useApi } from "../../../hooks/useApi";
import { useEspecialidades } from "../../../hooks/useEspecialidades";
import { billingService } from "../../../services/billing";
import { FaturaResumo } from "../../../types/billing.types";
import { temAssinaturaAtiva } from "../../../lib/checkoutIntent";
import { formatCurrency, formatDate, t } from "../../../i18n";
import { BillingPortalButton } from "./BillingPortalButton";
import { CancelSubscriptionDialog } from "./CancelSubscriptionDialog";
import { PATHS } from "../../../app/paths";

function statusTone(status: string | null | undefined): BadgeTone {
  if (status === "active" || status === "trialing" || status === "paid") return "success";
  if (status === "past_due" || status === "unpaid" || status === "uncollectible") return "danger";
  return "warning";
}

/** Boleto já gerado na página da fatura e ainda não compensado: a fatura continua "open". */
const boletoPending = (invoice: FaturaResumo) =>
  invoice.status === "open" && invoice.pagamento_status === "pending" && invoice.pagamento_metodo === "boleto";

/** Em aberto e já passou do vencimento. O backend decide (respeita o Test Clock). */
const overdue = (invoice: FaturaResumo) => Boolean(invoice.vencida && invoice.vencimento_em);

const Notice: React.FC<{ tone?: "info" | "warning"; children: React.ReactNode; action?: React.ReactNode }> = ({ tone = "info", children, action }) => (
  <div className={cn("flex flex-wrap items-center gap-3 rounded-card border px-4 py-3 text-caption text-fg", tone === "warning" ? "border-warning/30 bg-warning-soft" : "border-primary-line bg-primary-tint")}>
    <span className="min-w-0 flex-1">{children}</span>
    {action}
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <dt className="text-fg-muted">{label}</dt>
    <dd className="break-words font-medium text-fg">{children}</dd>
  </>
);

/** Detalhes do plano em Configurações: plano, avisos, cartão, faturas, trocar e cancelar. */
export const PlanDetails: React.FC<{ specialty?: string }> = ({ specialty }) => {
  const navigate = useNavigate();
  const { billing, setBilling } = useSession();
  const { canViewBilling, canManagePlan, isCourtesy } = useAccess();
  const invoicesApi = useApi<{ faturas: FaturaResumo[] }>();
  const cancelApi = useApi<{ status: string }>();
  const actionApi = useApi<{ status: string }>();
  const [invoices, setInvoices] = useState<FaturaResumo[]>([]);
  const [cancelOpen, setCancelOpen] = useState(false);

  const plan = billing?.plano;
  const cancelScheduled = Boolean(plan?.cancelar_no_fim_do_periodo);
  const inTrial = plan?.status === "trialing";
  const planKey = billing?.plano_chave ?? plan?.plano_chave;
  const freeLabel = planDetails.free.shortTitle;
  const specialtyName = useEspecialidades().find((e) => e.chave === specialty)?.nome;
  const card = billing?.cartao?.bandeira && billing.cartao.final ? billing.cartao : null;
  const showDetails = canViewBilling && (plan || card);
  const subscribed = temAssinaturaAtiva(billing) && Boolean(plan);

  useEffect(() => {
    if (!canViewBilling) return;
    invoicesApi
      .request(() => billingService.getFaturas(invoicesApi.apiFetch), { showErrorToast: false })
      .then((result) => { if (result) setInvoices(result.faturas); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewBilling]);

  // Recarrega a assinatura para o card refletir a ação (selo, plano Básico, troca).
  const reloadSubscription = async () => {
    const subscription = await billingService.getAssinatura(actionApi.apiFetch).catch(() => null);
    if (subscription) setBilling(subscription);
  };

  const cancel = async (now: boolean) => {
    const result = await cancelApi.request(
      () => billingService.postCancelar(cancelApi.apiFetch, { imediato: now }),
      { successMessage: now ? t("settings.plan.cancelled") : t("settings.plan.cancelledAtEnd") },
    );
    if (!result) return;
    setCancelOpen(false);
    await reloadSubscription();
  };

  const reactivate = async () => {
    const result = await actionApi.request(() => billingService.postReativar(actionApi.apiFetch), {
      successMessage: t("settings.plan.reactivated"),
    });
    if (result) await reloadSubscription();
  };

  const keepCurrentPlan = async () => {
    const result = await actionApi.request(() => billingService.deleteTrocaAgendada(actionApi.apiFetch), {
      successMessage: t("settings.plan.changeCancelled"),
    });
    if (result) await reloadSubscription();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="min-w-0">
          <p className="text-body font-bold text-fg">{planKey ? planoChaveLabel(planKey) : freeLabel}</p>
          <p className="text-caption text-fg-subtle">{specialtyName || t("settings.plan.noSpecialty")}</p>
        </div>
        <div className="flex flex-wrap justify-end gap-1.5">
          {canViewBilling && plan && <Badge tone={statusTone(plan.status)}>{assinaturaStatusLabel(plan.status)}</Badge>}
          {cancelScheduled && <Badge tone="warning">{t("settings.plan.cancelScheduled")}</Badge>}
          {isCourtesy && <Badge tone="primary">{t("settings.plan.clinicAccess")}</Badge>}
          {inTrial && <Badge tone="primary">{t("settings.plan.trial")}</Badge>}
        </div>
      </div>

      {canViewBilling && cancelScheduled && (
        <Notice
          tone="warning"
          action={canManagePlan && (
            <Button size="sm" variant="outline" onClick={reactivate} disabled={actionApi.loading}>{t("settings.plan.reactivate")}</Button>
          )}
        >
          {plan?.proxima_cobranca
            ? t("settings.plan.endsOn", { date: formatDate(plan.proxima_cobranca), free: freeLabel })
            : t("settings.plan.endsAtPeriodEnd", { free: freeLabel })}
        </Notice>
      )}

      {canViewBilling && inTrial && !cancelScheduled && plan?.trial_fim && (
        <Notice>{t("settings.plan.trialUntil", { date: formatDate(plan.trial_fim) })}</Notice>
      )}

      {canViewBilling && plan?.troca_agendada && (
        <Notice
          action={canManagePlan && (
            <Button size="sm" variant="outline" onClick={keepCurrentPlan} disabled={actionApi.loading}>{t("settings.plan.keepCurrent")}</Button>
          )}
        >
          {plan.troca_agendada.efetiva_em
            ? t("settings.plan.changeToOn", { plan: planoChaveLabel(plan.troca_agendada.plano_chave), date: formatDate(plan.troca_agendada.efetiva_em) })
            : t("settings.plan.changeTo", { plan: planoChaveLabel(plan.troca_agendada.plano_chave) })}
        </Notice>
      )}

      {showDetails && (
        <dl className="grid grid-cols-kv gap-x-3.5 gap-y-1.5 text-label">
          {plan?.proxima_cobranca && (
            <Row label={cancelScheduled ? t("settings.plan.accessUntil") : inTrial ? t("settings.plan.firstCharge") : t("settings.plan.nextCharge")}>
              {formatDate(plan.proxima_cobranca)}
            </Row>
          )}
          {card ? (
            <Row label={t("settings.plan.card")}>{card.bandeira} •••• {card.final}</Row>
          ) : (
            plan && !cancelScheduled && <Row label={t("settings.plan.card")}>{t("settings.plan.noCard")}</Row>
          )}
        </dl>
      )}

      {!plan && !planKey && (
        <ul className="flex flex-col gap-1.5">
          {planDetails.free.features.map((item) => (
            <li key={item} className="flex items-center gap-2 text-body text-fg">
              <LuCheck className="shrink-0 text-primary" aria-hidden /> {item}
            </li>
          ))}
        </ul>
      )}

      {canViewBilling && (
        <section className="flex flex-col gap-2">
          <h4 className="text-caption font-semibold uppercase tracking-wide text-fg-subtle">{t("settings.plan.invoices")}</h4>
          {invoices.length === 0 ? (
            <p className="text-caption text-fg-muted">{t("settings.plan.noInvoices")}</p>
          ) : (
            <ul className="divide-y divide-line">
              {invoices.map((invoice) => (
                <li key={invoice.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-caption text-fg-muted">{invoice.paga_em ? formatDate(invoice.paga_em) : "—"}</span>
                  <span className="flex flex-wrap items-center justify-end gap-2">
                    <span className="text-body font-semibold text-fg">{formatCurrency(invoice.valor_total_centavos)}</span>
                    <Badge tone={overdue(invoice) ? "danger" : statusTone(invoice.status)}>
                      {boletoPending(invoice)
                        ? t("settings.plan.boletoPending")
                        : overdue(invoice)
                          ? t("settings.plan.overdue", { date: formatDate(invoice.vencimento_em!) })
                          : faturaStatusLabel(invoice.status)}
                    </Badge>
                    {invoice.url_fatura && (
                      <a className="text-caption font-semibold text-primary hover:underline" href={invoice.url_fatura} target="_blank" rel="noreferrer">
                        {invoice.status === "open" && !boletoPending(invoice) ? t("settings.plan.pay") : t("settings.plan.view")}
                      </a>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* Mudar plano + cartão e faturas; sem assinatura, o portal vira "Escolher plano" em destaque. */}
      {canManagePlan && subscribed ? (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(PATHS.plans)}>{t("settings.plan.changePlan")}</Button>
          <BillingPortalButton variant="text" />
        </div>
      ) : (
        <BillingPortalButton variant="primary" />
      )}

      {canManagePlan && subscribed && (
        <div className="-mb-0.5 flex justify-end border-t border-line pt-3">
          <Button variant="danger-ghost" size="sm" onClick={() => setCancelOpen(true)}>
            {cancelScheduled ? t("settings.plan.cancelNow") : t("settings.plan.cancel")}
          </Button>
        </div>
      )}

      {cancelOpen && plan && (
        <CancelSubscriptionDialog
          planKey={plan.plano_chave}
          nextCharge={plan.proxima_cobranca}
          onlyNow={cancelScheduled}
          inTrial={inTrial}
          loading={cancelApi.loading}
          onConfirm={cancel}
          onClose={() => setCancelOpen(false)}
        />
      )}
    </div>
  );
};
